<template>
  <el-drawer
    :visible.sync="visible"
    destroy-on-close
    size="90%"
    title="Buscar calle / POI"
    z-index="100000"
  >
    <div class="drawer-content">
      <el-form size="mini" class="form-flex" @submit.native.prevent="handleSubmit">
        <el-form-item class="input" label="Nombre calle / POI">
          <el-input v-model="searchText" />
        </el-form-item>
        <el-button type="primary" class="search" native-type="submit">Buscar</el-button>
      </el-form>
      <div class="results">
        <div class="list">
          <div
            v-for="result in results"
            :key="result.id"
            class="clickable result"
            :class="{ selected: isSelected(result) }"
            @click="handleClickResult(result)"
          >
            <div class="result-content">
              <div class="icon">
                <i v-if="isPoi(result)" class="fas fa-2x fa-fw fa-map-marker"></i>
                <i v-else class="fas fa-2x fa-fw fa-road"></i>
              </div>
              <div class="content">
                <div class="result-title">{{ result.name }}</div>
                <div class="result-text">{{ result.formatted_address }}</div>
              </div>
              <div class="buttons">
                <el-button type="success" @click="handleUse(result)">Usar</el-button>
              </div>
            </div>
            <el-form
              v-if="isEditing(result)"
              class="result-form"
              size="mini"
              @submit.native.prevent="handleCreate"
            >
              <el-form-item label="Central" :class="formItemErrorClass($v.formData.centralId)">
                <workgroup-central-selector
                  v-model="centralSelectorValue"
                  show-centrals
                  :allowed-workgroup="wgId"
                  :allow-select-workgroups="false"
                  @input="handleCentralChange"
                />
                <ErrorMessage :data="$v.formData.centralId" validation="required" />
              </el-form-item>
              <el-form-item label="Tipo">
                <el-radio-group v-model="formData.poi">
                  <el-radio-button label="1">POI</el-radio-button>
                  <el-radio-button label="0">Calle</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="Nombre" :class="formItemErrorClass($v.formData.name)">
                <el-input v-model="formData.name" />
                <ErrorMessage :data="$v.formData.name" validation="required" />
              </el-form-item>
              <el-form-item label="Indicación" :class="formItemErrorClass($v.formData.hint)">
                <el-input v-model="formData.hint" />
                <ErrorMessage :data="$v.formData.hint" validation="required" />
              </el-form-item>
              <el-form-item
                v-if="formData.poi === '0'"
                label="Número"
                :class="formItemErrorClass($v.formData.number)"
              >
                <el-input v-model="formData.number" @change="handleChangeNumber" />
                <ErrorMessage :data="$v.formData.number" validation="required" />
              </el-form-item>
              <el-button native-type="submit" type="primary"> Crear y usar</el-button>
            </el-form>
          </div>
        </div>
        <div class="map">
          <SimpleMap height="calc(100vh - 160px)" :lat="lat" :lng="lng" :zoom="16" />
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import drawer from "@/mixins/drawer";

import { getGeocode, getGeocodeByText, searchPlace } from "@/utils/googlemaps.ts";
import { v4 as idGenerator } from "uuid";
import { mapActions, mapGetters } from "vuex";
import SimpleMap from "@/components/SimpleMap/index.vue";
import WorkgroupCentralSelector from "@/components/common/forms/WorkgroupCentralSelector.vue";
import { required, requiredIf } from "vuelidate/lib/validators";
import { createStreet, createStreetPortal } from "@/api/operations/streets";
import { getDeliveryZones } from "@/api/operations/deliveryzones";
import { isPointInPolygon } from "geolib";
import ErrorMessage from "@/components/common/forms/ErrorMessage.vue";
import errors from "@/mixins/errors";
import { centralContainingCoords } from "@/api/operations/centrals";

export default {
  name: "OnlineStreetSearchDrawer",

  mixins: [drawer, errors],

  computed: {
    ...mapGetters("app", [
      "defaultWorkgroupCoords",
      "centralsByWorkgroupId",
      "defaultSelectedWorkgroupAndCentral",
      "centrals",
    ]),
    lat() {
      return (
        this.selectedResult?.geometry?.location?.lat() || this.defaultWorkgroupCoords?.[1] || 0
      );
    },
    lng() {
      return (
        this.selectedResult?.geometry?.location?.lng() || this.defaultWorkgroupCoords?.[0] || 0
      );
    },
  },
  props: {
    initialSearch: String,
    wgId: Number,
  },
  data() {
    return {
      centralSelectorValue: [],
      areas: [],
      formData: {
        centralId: null,
        poi: "",
        name: "",
        namePronunciation: "",
        hint: "",
        hintPronunciation: "",
        number: "",
      },
      searchText: "",
      selectedResult: null,
      selectedEdit: null,
      selectedGeocode: null,
      results: [],
    };
  },
  validations: {
    formData: {
      centralId: { required },
      name: { required },
      hint: { required },
      number: { required: requiredIf((model) => model.poi === "0") },
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.centralSelectorValue = [];
      }
      if (this.initialSearch) {
        this.results = [];
        this.selectedResult = null;
        this.searchText = this.initialSearch;
        this.handleSubmit();
      }
    },
    initialSearch() {
      this.searchText = this.initialSearch;
      this.handleSubmit();
    },
    "formData.centralId": {
      handler() {
        if (this.formData.centralId) {
          const central = this.resolveCentralName(this.formData.centralId);
          this.formData.hint = this.formData.hint.match(/ - .*$/)
            ? this.formData.hint.replace(/ - .*/, ` - ${central}`)
            : `${this.formData.hint} - ${central}`;
        }
      },
      deep: true,
    },
  },
  created() {
    this.loadAreas();
  },
  methods: {
    ...mapActions("clientStreets", ["getStreets", "getStreetsNearestPortal3"]),
    resolveCentralName(centralId) {
      return this.centrals.find((c) => c.id === centralId)?.name;
    },
    async streetExpectedCentralId() {
      const coords = [this.lng, this.lat];
      return await centralContainingCoords(this.wgId, coords);
    },
    handleCentralChange() {
      this.formData.centralId = this.centralSelectorValue[0]?.centralId;
      this.$v.formData.centralId.$reset();
    },
    async handleSubmit() {
      this.results = (
        await searchPlace(this.searchText, {
          lat: this.defaultWorkgroupCoords[1],
          lng: this.defaultWorkgroupCoords[0],
        })
      ).map((i) => ({
        ...i,
        id: idGenerator(),
      }));
      if (this.results.length > 0) {
        this.selectedResult = this.results[0];
        this.$forceUpdate();
      }
    },
    handleClickResult(result) {
      if (this.selectedResult?.id !== result?.id) {
        this.selectedResult = result;
        this.selectedEdit = null;
      }
    },
    isSelected(result) {
      return this.selectedResult?.id === result?.id;
    },
    isEditing(result) {
      return this.selectedEdit?.id === result?.id;
    },
    isPoi(item) {
      return (
        !item.types.includes("street_address") &&
        !item.types.includes("premise") &&
        !item.types.includes("route")
      );
    },
    async handleChangeNumber() {
      if (this.selectedGeocode) {
        const { address_components } = this.selectedGeocode;
        const street = address_components.find((c) => c.types.includes("route"))?.short_name;
        const locality = address_components.find((c) => c.types.includes("locality"))?.short_name;
        this.selectedGeocode = await getGeocodeByText(street, locality, this.formData.number);
        const point = [this.lat, this.lng];
        const area = this.areas.find((area) => isPointInPolygon(point, area.polygon));
        this.formData.idDelivery = area?.idDelivery;
        this.selectedResult.geometry = this.selectedGeocode.geometry;
      }
    },
    async handleUse(result) {
      const geocode = await getGeocode(result.place_id);
      this.selectedGeocode = geocode;
      const { address_components } = geocode;

      const street = address_components.find((c) => c.types.includes("route"))?.short_name || "";
      const street_number =
        address_components.find((c) => c.types.includes("street_number"))?.short_name || "";
      const locality =
        address_components.find((c) => c.types.includes("locality"))?.short_name ||
        address_components.find((c) => c.types.includes("administrative_area_level_1"))
          ?.short_name ||
        address_components.find((c) => c.types.includes("administrative_area_level_2"))
          ?.short_name ||
        "";

      const point = [this.lat, this.lng];
      const area = this.areas.find((area) => isPointInPolygon(point, area.polygon));

      let name = result.name || "";
      let number = "";

      if (!this.isPoi(result)) {
        name = street || result.name || "";
        number = street_number || "0";
      }

      this.formData = {
        centralId: this.formData.centralId,
        poi: this.isPoi(result) ? "1" : "0",
        name: name,
        hint: this.isPoi(result)
          ? street && street_number
            ? `${street} ${street_number}`
            : result.formatted_address || ""
          : locality || result.formatted_address || "",
        idDelivery: area?.idDelivery,
        number: number || "0",
      };

      this.selectedEdit = result;
    },
    async getOrCreateStreet(street, poi) {
      const expectedName = street.hint ? `${street.name} (${street.hint})` : street.name;
      const existingStreets = await this.getStreets({
        wgId: this.wgId,
        text: street.name,
      });
      const matchedStreets = existingStreets.filter(
        (s) => s.poi === parseInt(poi) && s.value === expectedName
      );
      if (matchedStreets.length > 0) {
        return matchedStreets[0];
      }
      if (this.$v.$invalid) {
        this.$message({
          type: "error",
          message: "Hay fallos en el formulario",
        });
        return;
      }

      const streetCentralId = await this.streetExpectedCentralId();
      if (streetCentralId && streetCentralId !== this.formData.centralId) {
        const streetType = poi === "1" ? "el POI" : "la calle";
        try {
          await this.$confirm(
            `¿Estás seguro de que quieres crear ${streetType} en ${this.resolveCentralName(this.formData.centralId)}? Parece que pertenece a la central de ${this.resolveCentralName(streetCentralId)}`,
            "Confirmación de central",
            {
              confirmButtonText: "Sí",
              cancelButtonText: "No",
            }
          );
        } catch (e) {
          throw Error("Central not contained");
        }
      }
      return (
        await createStreet(this.wgId, street.centralId, {
          ...street,
          poi: poi === "1" ? 1 : 0,
        })
      ).data;
    },
    async getOrCreatePortal(street, portalNumber, coords, idDelivery) {
      const { id: streetId, centralId } = street;
      let match = null;

      try {
        const data = await this.getStreetsNearestPortal3({
          wgId: this.wgId,
          streetId,
          portalNumber,
        });
        match = data.find((d) => d.number === portalNumber);
      } catch (e) {
        console.error(e);
      }

      if (!match) {
        await createStreetPortal(this.wgId, centralId, streetId, {
          number: parseInt(portalNumber),
          coords,
          idDelivery,
        });
      }
    },
    async handleCreate() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const coords = [this.lng, this.lat];
        const { number, idDelivery, poi, ...street } = this.formData;
        const targetStreet = await this.getOrCreateStreet(street, poi);
        const portalNumber = poi === "0" ? number : "0";
        await this.getOrCreatePortal(targetStreet, portalNumber, coords, idDelivery);
        this.$emit("created", targetStreet.id, number);
      }
    },
    async loadAreas() {
      if (this.areas.length === 0) {
        this.areas = await getDeliveryZones(this.wgId);
      }
    },
  },
  components: { SimpleMap, ErrorMessage, WorkgroupCentralSelector },
};
</script>

<style scoped>
.form-flex {
  display: flex;
  width: 100%;
  align-items: center;
}

.change {
  flex: 1;
}

.search {
  margin-top: 19px;
  margin-left: 10px;
}

.results {
  display: grid;
  grid-template-columns: 1fr 2fr;
  line-height: 20px;
}

.list {
  height: calc(100vh - 160px);
  overflow-y: auto;
}

.result {
  display: flex;
  flex-direction: column;
  background-color: #eee;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 10px;
  margin-right: 10px;
  transition: height 0.5s ease-in-out;
}

.result-content {
  display: flex;
  align-items: center;
}

.result-form {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #ccc;
}

.icon {
  width: 46px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.selected {
  background-color: #ddd;
}

.result-title {
  font-size: 12px;
  padding: 0;
  font-weight: bold;
}

.result-text {
  font-size: 12px;
}

.buttons {
  margin-left: auto;
}

.map {
  height: 100%;
}
</style>
